import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid2'
import React from 'react'
import { CustomTooltip } from './CustomTooltip'

/** @notExported */
interface IAutoCalculatedInfoProps {
  /** Explanatory text shown to the user. */
  text: string
  /** 'tooltip' shows a compact info icon whose hover reveals the text (e.g. table column headers).
   * 'inline' shows the icon with the text next to it (e.g. inside a form or modal). Defaults to 'tooltip'. */
  variant?: 'tooltip' | 'inline'
}

/**
 * Small info icon explaining that a value is calculated automatically rather than entered
 * directly, either as a hover tooltip (compact) or inline text with an icon.
 *
 * @returns Info icon/note component.
 * @notExported
 */
const AutoCalculatedInfo: React.FC<IAutoCalculatedInfoProps> = ({ text, variant = 'tooltip' }) => {
  if (variant === 'inline') {
    return (
      <Grid container alignItems="flex-start" spacing={0.5} sx={{ mt: 1, mb: 1 }}>
        <Grid sx={{ display: 'flex', alignItems: 'center' }}>
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Grid>
        <Grid sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {text}
          </Typography>
        </Grid>
      </Grid>
    )
  }

  return (
    <CustomTooltip title={text} placement="top">
      <InfoOutlinedIcon
        fontSize="small"
        color="action"
        sx={{ verticalAlign: 'middle', ml: 0.5, cursor: 'help' }}
        data-testid="auto-calculated-info-icon"
        onClick={event => event.stopPropagation()}
      />
    </CustomTooltip>
  )
}

export default AutoCalculatedInfo
